import type { FunctionComponent, KeyboardEvent, MouseEvent, PropsWithoutRef, Ref } from 'react';
import { useContext, forwardRef, useEffect, useMemo, useRef, useState, Fragment } from 'react';

import SearchInput from '../SearchInput';
import Flex from '../Flex';
import Link from '../Link';
import type { ForwardProps } from '../../types';
import {
  useAfterInitialEffect,
  useConsolidatedRef,
  useElement,
  useEscape,
  useI18n,
  useTheme,
  useUID,
  useRefMap
} from '../../hooks';
import * as arrowMicroDownIcon from '../Icon/icons/arrow-micro-down.icon';
import { registerIcon } from '../Icon/Icon';
import type { MenuItemProps, MenuProps } from '../Menu';
import { createStringMatcher, debounce } from '../../utils';

import TabsContext from './TabsContext';
import {
  StyledErrorIcon,
  StyledAllTabsButton,
  StyledTabs,
  StylesAllTabsButtonWrapper,
  StyledAllTabsWrapper,
  StyledSeparator
} from './Tabs.styles';
import type { TabsProps } from './Tabs.types';
import Tab from './Tab';

registerIcon(arrowMicroDownIcon);

type TabItem = TabsProps['tabs'][number];

const AllTabsButton = ({
  tabs,
  tabRefMap,
  currentTabId,
  onTabClick
}: Pick<TabsProps, 'tabs' | 'currentTabId' | 'onTabClick'> & {
  tabRefMap: Map<string, HTMLAnchorElement | HTMLButtonElement | null>;
}) => {
  const menuId = useUID();
  const t = useI18n();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');
  const searchEleRef = useRef<HTMLInputElement>(null);
  const [focusControlEl, setFocusControlEl] = useState<MenuProps['focusControlEl']>();

  const searchRegex = createStringMatcher(search);

  const itemsToRender = useMemo(() => {
    return tabs
      .filter(({ name }) => searchRegex.test(name))
      .map(({ id, name, count, disabled, errors }) => {
        const selected = id === currentTabId;
        const item: MenuItemProps = { id, primary: name, selected, count, disabled };
        if (errors) {
          item.visual = <StyledErrorIcon name='warn-solid' />;
        }
        return item;
      });
  }, [tabs, search, currentTabId]);

  useEscape(() => {
    if (!search) {
      menuButtonRef.current?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
      menuButtonRef.current?.focus();
    }
  }, searchEleRef);

  return (
    <StyledAllTabsButton
      ref={menuButtonRef}
      text={t('all_tabs')}
      iconOnly
      variant='simple'
      icon='arrow-micro-down'
      menu={{
        listId: menuId,
        mode: 'single-select',
        items: itemsToRender,
        focusControlEl,
        accent: search ? searchRegex : undefined,
        onItemClick: (id: string, event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
          onTabClick?.(id, event);
          setSearch('');
          tabRefMap.get(id)?.focus();
        },
        header: (
          <SearchInput
            ref={searchEleRef}
            onSearchChange={setSearch}
            value={search}
            role='searchbox'
            aria-controls={menuId}
          />
        )
      }}
      popover={{
        onHide: () => {
          setFocusControlEl(undefined);
          setSearch('');
        },
        onShow: () => {
          if (searchEleRef.current) {
            setFocusControlEl(searchEleRef.current);
            searchEleRef.current.focus();
          }
        }
      }}
    />
  );
};

/**
 * The tabs component will take a list of tab data objects and render out a list of tabs in either vertical or horizontal direction.
 * The tabs component handles which tab is active but will give you a callback function that allows you to make changes when a new tab is activated
 */
const Tabs: FunctionComponent<TabsProps & ForwardProps> = forwardRef(function Tabs(
  {
    tabs = [],
    type = 'horizontal',
    currentTabId,
    onTabClick,
    ...restProps
  }: PropsWithoutRef<TabsProps>,
  ref: Ref<HTMLElement>
) {
  const [wrapper, setWrapper] = useElement<HTMLDivElement>();
  const wrapperRef = useConsolidatedRef(ref, setWrapper);
  const { level } = useContext(TabsContext);
  const [hasScrollBar, setHasScrollBar] = useState(false);
  const {
    base: { spacing: themeSpacing },
    components: {
      tabs: { detached: tabsDetached }
    }
  } = useTheme();

  // Used to keep a reference for every tab in order to focus them with key presses
  const [tabRefMap, getTabRef] = useRefMap<HTMLButtonElement | HTMLAnchorElement | null>();

  // Handles arrow keypresses for changing the focus
  const changeTabFocus = (event: KeyboardEvent, index: number): void => {
    if (tabs.length) {
      const { key } = event;
      if (key === 'ArrowRight' || key === 'ArrowDown') {
        const nextIndex = index + 1 < tabs.length ? index + 1 : 0;
        const nextTab = tabRefMap.get(tabs[nextIndex].id) as HTMLButtonElement;

        if (!nextTab?.disabled) {
          nextTab?.focus();
        } else {
          changeTabFocus(event, nextIndex);
        }
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        const prevIndex = index === 0 ? tabs.length - 1 : index - 1;
        const prevTab = tabRefMap.get(tabs[prevIndex].id) as HTMLButtonElement;
        if (!prevTab?.disabled) {
          prevTab?.focus();
        } else {
          changeTabFocus(event, prevIndex);
        }
      }
    }
  };

  useAfterInitialEffect(() => {
    const event = new Event('cosmos-tab-changed', { bubbles: true, composed: true });
    wrapperRef.current?.dispatchEvent(event);

    const currentTabEl = tabRefMap.get(currentTabId);
    currentTabEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [currentTabId]);

  useEffect(() => {
    const currentTabEl = tabRefMap.get(currentTabId);

    if (!currentTabEl || !wrapper) return;

    // raf to ensure layout is stable before reading offset values
    const rafId = requestAnimationFrame(() => {
      if (type === 'horizontal') {
        // Horizontal tabs need left position and width for sliding animation
        const left = currentTabEl.offsetLeft;
        const width = currentTabEl.offsetWidth;
        wrapper.style.setProperty('--indicator-left', `${left}px`);
        wrapper.style.setProperty('--indicator-width', `${width}px`);
      } else {
        // Vertical tabs need height and top position for sliding animation
        const height = currentTabEl.offsetHeight;
        const top = currentTabEl.offsetTop;
        wrapper.style.setProperty('--indicator-height', `${height}px`);
        wrapper.style.setProperty('--indicator-top', `${top}px`);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [currentTabId, type, tabRefMap, wrapper, tabsDetached, themeSpacing]);

  useEffect(() => {
    if (!wrapper) return;

    const ro = new ResizeObserver(
      debounce(([entry]) => {
        if (!entry?.target.isConnected || !(entry.target instanceof HTMLElement)) return;

        setHasScrollBar(entry.target.scrollWidth > entry.target.offsetWidth);
      }, 1)
    );

    ro.observe(wrapper);

    return () => {
      ro.disconnect();
    };
  }, [wrapper]);

  const shouldRenderAllTabsButton = type === 'horizontal' && hasScrollBar;

  const TabList = (
    <Flex
      {...restProps}
      container={{
        direction: type !== 'horizontal' ? 'column' : undefined
      }}
      item={{ shrink: 0 }}
      as={StyledTabs}
      ref={wrapperRef}
      role='tablist'
      aria-orientation={type}
      hasScrollBar={hasScrollBar}
      level={level}
    >
      {tabs.map((tab: TabItem, i) => {
        const { href, name, count, id, disabled, errors } = tab;
        const selected = id === currentTabId;

        return (
          <Fragment key={id}>
            {level > 1 && !tabsDetached && (
              <StyledSeparator
                transparent={
                  selected ||
                  (i === 0 ? !shouldRenderAllTabsButton : tabs[i - 1].id === currentTabId)
                }
              />
            )}
            <Tab
              id={id}
              aria-selected={selected}
              aria-controls={`${id}-panel`}
              content={name}
              count={count}
              href={href}
              selected={selected}
              onClick={(event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
                onTabClick?.(id, event);
              }}
              role='tab'
              ref={getTabRef(id)}
              onKeyDown={(e: KeyboardEvent) => {
                if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) {
                  e.preventDefault();
                  changeTabFocus(e, i);
                }
              }}
              as={href ? Link : undefined}
              tabIndex={selected ? undefined : -1}
              aria-label={name}
              type={type}
              disabled={disabled}
              errors={errors}
            />
          </Fragment>
        );
      })}
    </Flex>
  );

  return shouldRenderAllTabsButton ? (
    <Flex container={{ alignItems: 'stretch' }} as={StyledAllTabsWrapper} level={level}>
      <Flex container as={StylesAllTabsButtonWrapper}>
        <AllTabsButton
          tabs={tabs}
          tabRefMap={tabRefMap}
          currentTabId={currentTabId}
          onTabClick={onTabClick}
        />
      </Flex>
      {TabList}
    </Flex>
  ) : (
    TabList
  );
});

export default Tabs;
